import React, { useEffect, useState } from 'react';
import { Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import 'translations/translations';
import { organizationActions } from 'actions/organizationActions';
import { ApplicationState } from 'store';
import { DateRangePickerComponent } from 'components/DateRangePicker';
import {
  LevelOfInvoiceEnumDto,
  SettlementTypeEnumDto,
  AccountingEnumDto,
  CurrencyEnumDto,
} from '.generated/api';
import LevelOfInvoiceSelect from 'features/common/LevelOfInvoiceSelect';
import SettlementTypeSelect from 'features/common/SettlementTypeSelect';
import AccountingSelect from 'features/common/AccountingSelect';
import CurrencySelect from 'features/common/CurrencySelect';

import { customerNumberRE, guaranteedVolumeRE } from 'helpers/validation';

type StepProps = {
  stepTitleString: string;
  buttonText: string;
};

const useStyles = makeStyles((theme) => ({
  topMargin: {
    marginTop: theme.spacing(3),
  },
  datePickerItem: {
    width: '50%',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

const AddContractInfoStep: React.FC<StepProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const classes = useStyles();

  const organizationId = useSelector(
    (state: ApplicationState) => state.organization?.id,
  );

  const organizationError = useSelector(
    (state: ApplicationState) => state.organization?.error,
  );

  const [inputs, setInputs] = useState({
    customerNumber: '',
    startDate: '',
    endDate: '',
    levelOfInvoice: '',
    fullMeasurementPrice: '',
    pieceMeasurementPrice: '',
    settlementType: '',
    accounting: '',
    currency: '',
    guaranteedVolume: '',
  });

  const handleCustomerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (!customerNumberRE.test(value)) {
      return;
    }
    setInputs((input) => ({ ...input, customerNumber: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const onStartDateChange = (dateValue?: Date) => {
    if (dateValue) {
      setInputs((inputs) => ({
        ...inputs,
        startDate: dateValue.toISOString(),
      }));
    } else {
      setInputs((inputs) => ({
        ...inputs,
        startDate: '',
      }));
    }
  };

  const onEndDateChange = (dateValue?: Date) => {
    if (dateValue) {
      setInputs((inputs) => ({
        ...inputs,
        endDate: dateValue.toISOString(),
      }));
    } else {
      setInputs((inputs) => ({
        ...inputs,
        endDate: '',
      }));
    }
  };

  const handleDateRangeChange = (
    from: Date | null,
    to: Date | null,
    valid: boolean,
  ) => {
    onStartDateChange(from && valid ? from : undefined);
    onEndDateChange(to && valid ? to : undefined);
  };

  const handleGuaranteedVolumeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (!guaranteedVolumeRE.test(value)) {
      return;
    }
    setInputs((input) => ({ ...input, guaranteedVolume: value }));
  };

  const callbackFn = () => {
    if (
      organizationId &&
      (inputs.startDate || inputs.endDate || inputs.customerNumber)
    ) {
      dispatch(
        organizationActions.addContractualInfo(organizationId, {
          startDate: inputs.startDate,
          endDate: inputs.endDate,
          customerNumber:
            inputs?.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT ||
            inputs.customerNumber === ''
              ? null
              : inputs.customerNumber,
          levelOfInvoice: inputs.levelOfInvoice as LevelOfInvoiceEnumDto,
          fullMeasurementPrice: parseFloat(inputs.fullMeasurementPrice),
          pieceMeasurementPrice: parseFloat(inputs.pieceMeasurementPrice),
          settlementType: inputs.settlementType as SettlementTypeEnumDto,
          accounting: inputs.accounting as AccountingEnumDto,
          currency: inputs.currency as CurrencyEnumDto,
          guaranteedVolume: inputs.guaranteedVolume,
        }),
      );
    } else {
      dispatch({ type: 'SET_STEP', step: 3 });
    }
  };

  useEffect(() => {
    dispatch({ type: 'SET_CALLBACK', callbackFn });
  });

  useEffect(() => {
    if (
      inputs.startDate.length > 0 &&
      inputs.endDate.length > 0 &&
      inputs.levelOfInvoice.length > 0 &&
      inputs.fullMeasurementPrice.length > 0 &&
      inputs.pieceMeasurementPrice.length > 0 &&
      inputs.settlementType.length > 0 &&
      inputs.accounting.length > 0 &&
      inputs.currency.length > 0 &&
      inputs.guaranteedVolume.length > 0
    ) {
      dispatch({ type: 'DISABLE_BUTTON', disabled: false });
    } else {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
    }
  }, [inputs]);

  return (
    <>
      <Grid item xs={12}>
        <LevelOfInvoiceSelect onChange={handleChange} required />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={t(
            'organizations_page.create_org.add_contract_info_step.customer_no',
          )}
          name="customerNumber"
          onChange={handleCustomerNumberChange}
          value={
            inputs.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT
              ? ''
              : inputs.customerNumber
          }
          disabled={inputs.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT}
        ></TextField>
      </Grid>
      <Grid item xs={12} className={classes.topMargin}>
        <TextField
          variant="outlined"
          fullWidth
          type="number"
          label={t(
            'organizations_page.create_org.add_contract_info_step.price_full_measurement',
          )}
          InputProps={{ inputProps: { min: 0 } }}
          name="fullMeasurementPrice"
          onChange={handleChange}
          required
        ></TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          type="number"
          label={t(
            'organizations_page.create_org.add_contract_info_step.price_piece_measurement',
          )}
          InputProps={{ inputProps: { min: 0 } }}
          name="pieceMeasurementPrice"
          onChange={handleChange}
          required
        ></TextField>
      </Grid>
      <Grid item xs={12}>
        <SettlementTypeSelect onChange={handleChange} required />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={t(
            'organizations_page.create_org.add_contract_info_step.guaranteed_volume',
          )}
          name="guaranteedVolume"
          onChange={handleGuaranteedVolumeChange}
          value={inputs.guaranteedVolume}
        ></TextField>
      </Grid>
      <Grid item xs={12}>
        <AccountingSelect onChange={handleChange} required />
      </Grid>
      <Grid item xs={12}>
        <CurrencySelect onChange={handleChange} required />
      </Grid>
      <Grid item xs={12} className={classes.topMargin}>
        <Grid container spacing={2}>
          <DateRangePickerComponent
            utc
            defaultStartDate={null}
            defaultEndDate={null}
            onChange={handleDateRangeChange}
            itemClass={classes.datePickerItem}
          />
        </Grid>
      </Grid>
      <Typography className={classes.error}>
        {!!organizationError && organizationError}
      </Typography>
    </>
  );
};

export { AddContractInfoStep };
